import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Select, Alert, Typography } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, DownOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph, Text } = Typography;

function Register() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { register } = useAuth();
    const navigate = useNavigate();

    const onFinish = async (values) => {
        setLoading(true);
        setError('');

        if (values.password !== values.password_confirmation) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        try {
            const result = await register(values);
            if (result.success) {
                const { user_type } = result.user;
                if (user_type === 'admin') {
                    navigate('/admin/dashboard');
                } else if (user_type === 'vendor') {
                    navigate('/vendor/dashboard');
                } else {
                    navigate('/dashboard');
                }
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] py-8 px-4 sm:px-6 lg:px-8">
            <div className="w-full max-w-md">
                {/* Header Section */}
                <div className="text-center mb-8">
                    {/* Logo */}
                    <div className="mb-6 flex items-center justify-center gap-2">
                        <div className="w-8 h-8 bg-[#C97D60] rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                            </svg>
                        </div>
                        <h1 className="text-4xl font-bold text-[#2C2C2C]" style={{ fontFamily: 'var(--font-serif)' }}>
                            Spoon & Spice
                        </h1>
                    </div>
                    <p className="text-base text-[#4A4A4A] mb-6">Join our culinary community</p>
                    
                    {/* Heading with icon */}
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <div className="w-6 h-6 bg-[#C97D60] rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                            </svg>
                        </div>
                        <Title level={2} className="!mb-0 !text-[#2C2C2C]" style={{ fontFamily: 'var(--font-serif)' }}>Create your account</Title>
                    </div>
                    <Paragraph className="text-[#4A4A4A] mb-0">
                        Already have an account?{' '}
                        <Link to="/login" className="text-[#C97D60] hover:text-[#B8735A] font-medium transition-colors">
                            Sign in here
                        </Link>
                    </Paragraph>
                </div>

                {/* Form Card */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    <div className="px-8 py-8">
                        {error && (
                            <Alert 
                                type="error" 
                                message={error} 
                                showIcon 
                                className="mb-6 rounded-lg"
                            />
                        )}

                        <Form
                            layout="vertical"
                            onFinish={onFinish}
                            className="space-y-5"
                            size="large"
                        >
                            <Form.Item
                                label={<span className="text-[#2C2C2C] font-medium">* Full Name</span>}
                                name="name"
                                rules={[{ required: true, message: 'Please enter your full name' }]}
                                className="mb-5"
                            >
                                <Input 
                                    placeholder="Enter your full name" 
                                    className="h-12 rounded-lg border-gray-300 focus:border-[#C97D60] focus:ring-[#C97D60]"
                                    suffix={<UserOutlined className="text-[#4A4A4A]" />}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<span className="text-[#2C2C2C] font-medium">* Email Address</span>}
                                name="email"
                                rules={[
                                    { required: true, message: 'Please enter your email address' },
                                    { type: 'email', message: 'Please enter a valid email address' },
                                ]}
                                className="mb-5"
                            >
                                <Input 
                                    placeholder="Enter your email address" 
                                    autoComplete="email"
                                    className="h-12 rounded-lg border-gray-300 focus:border-[#C97D60] focus:ring-[#C97D60]"
                                    suffix={<MailOutlined className="text-[#4A4A4A]" />}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<span className="text-[#2C2C2C] font-medium">* Phone Number</span>}
                                name="phone"
                                rules={[{ required: true, message: 'Please enter your phone number' }]}
                                className="mb-5"
                            >
                                <Input 
                                    placeholder="Enter your phone number" 
                                    className="h-12 rounded-lg border-gray-300 focus:border-[#C97D60] focus:ring-[#C97D60]"
                                    suffix={<PhoneOutlined className="text-[#4A4A4A]" />}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<span className="text-[#2C2C2C] font-medium">* Account Type</span>}
                                name="user_type"
                                initialValue="customer"
                                rules={[{ required: true, message: 'Please select your account type' }]}
                                className="mb-5"
                            >
                                <Select
                                    options={[
                                        { label: 'Customer - Order delicious food', value: 'customer' },
                                        { label: 'Vendor - Serve amazing dishes', value: 'vendor' },
                                    ]}
                                    placeholder="Select your account type"
                                    className="h-12 rounded-lg"
                                    suffixIcon={<DownOutlined className="text-[#4A4A4A]" />}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<span className="text-[#2C2C2C] font-medium">* Password</span>}
                                name="password"
                                rules={[
                                    { required: true, message: 'Please enter your password' },
                                    { min: 6, message: 'Password must be at least 6 characters' }
                                ]}
                                className="mb-5"
                            >
                                <Input.Password 
                                    placeholder="Create a strong password" 
                                    autoComplete="new-password"
                                    className="h-12 rounded-lg border-gray-300 focus:border-[#C97D60] focus:ring-[#C97D60]"
                                    iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<span className="text-[#2C2C2C] font-medium">* Confirm Password</span>}
                                name="password_confirmation"
                                rules={[{ required: true, message: 'Please confirm your password' }]}
                                className="mb-6"
                            >
                                <Input.Password 
                                    placeholder="Confirm your password" 
                                    autoComplete="new-password"
                                    className="h-12 rounded-lg border-gray-300 focus:border-[#C97D60] focus:ring-[#C97D60]"
                                    iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                                />
                            </Form.Item>

                            <Form.Item className="mb-6">
                                <Button 
                                    type="primary" 
                                    htmlType="submit" 
                                    loading={loading} 
                                    block
                                    className="h-12 rounded-lg bg-[#C97D60] border-0 font-semibold text-base hover:bg-[#B8735A] transition-all duration-200 shadow-md hover:shadow-lg"
                                >
                                    {loading ? 'Creating account...' : 'Create Account'}
                                </Button>
                            </Form.Item>
                        </Form>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default Register;
